import { useCurrentAccount, useDeleteAccount } from '@application/api';
import { MtSocialProvidersController, useChangePasswordModal } from '@application/components';
import { useModal, useShowToast } from '@application/utils';
import type { ContactPreference } from '@gql/graphql';
import { MtDeleteAccountModal, MtSocialProviderType } from '@presentation/components';
import type { MtSettingsBuyerPageData, MtSettingsBuyerPageFormValues } from '@presentation/pages';
import { MtSettingsBuyerPage } from '@presentation/pages';
import { FormikProvider, useFormik } from 'formik';
import { cloneDeep } from 'lodash/fp';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from '@presentation/utils';
import { useGetProfile } from './api/useGetProfile';
import { useProfileInitialValuesBuilder } from './api/useProfileInitialValuesBuilder';
import { useUpdateProfile } from './api/useUpdateProfile';
import { useValidationSchema } from './useValidationSchema';

export const MtSettingsBuyerPageController: FC = () => {
  const [mode, setMode] = useState<MtSettingsBuyerPageData['mode']>('view');
  const { currentAccountAuthType } = useCurrentAccount();
  const { showToast } = useShowToast();
  const { translate } = useTranslation();

  const { profile, profileLoading } = useGetProfile();
  const { initialValues } = useProfileInitialValuesBuilder(profile);
  const { validationSchema } = useValidationSchema();

  const { updateProfileFromForm } = useUpdateProfile();
  const { deleteAccountLoading, deleteModalForm } = useDeleteAccount();

  const { show: showChangePasswordModal } = useChangePasswordModal();
  const { show: showDeleteAccountModal, modal: deleteModal } = useModal();

  const form = useFormik<MtSettingsBuyerPageFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await updateProfileFromForm(values);
      showToast(translate('common.toasts.title.success'), { type: 'success', autoClose: 1500 });
      setMode('view');
    },
  });

  const onChangeContactPreferenceClick = useCallback(
    (contactPreferences: ContactPreference[], value: boolean) => {
      let currentPreferences = cloneDeep(form.values?.contactPreferences ?? null);
      if (!currentPreferences) return;
      if (value) {
        currentPreferences = [...new Set(currentPreferences.concat(contactPreferences))];
      } else {
        currentPreferences = currentPreferences.filter(
          (item) => !contactPreferences.includes(item),
        );
      }
      form.setFieldValue('contactPreferences', currentPreferences);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.values?.contactPreferences],
  );

  return (
    <>
      <FormikProvider value={deleteModalForm}>
        <MtDeleteAccountModal isLoading={deleteAccountLoading} modal={deleteModal} />
      </FormikProvider>
      <FormikProvider value={form}>
        <MtSettingsBuyerPage
          loading={deleteAccountLoading || profileLoading}
          mode={mode}
          onEditClick={() => setMode('edit')}
          onCancelClick={() => setMode('view')}
          showChangePassword={currentAccountAuthType === MtSocialProviderType.FIREBASE}
          onChangePasswordClick={() => showChangePasswordModal()}
          onDeleteAccountClick={() => showDeleteAccountModal()}
          socialProvidersComponent={<MtSocialProvidersController />}
          onChangeContactPreferenceClick={onChangeContactPreferenceClick}
        />
      </FormikProvider>
    </>
  );
};
